import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, Switch, Tab, Tabs } from "@mui/material";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

const TRAINING_ESSENTIAL_TYPES = ["Nutrition", "Hydration", "Meal Prep", "Recovery", "Apparel"];
const ROWS_PER_PAGE = 10;

const TrainingEssentialsManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [sortOrder, setSortOrder] = useState({ name: "", direction: "asc" });
  const [write, setWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteState, setDeleteState] = useState({ status: false, id: "" });
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    const matchingNavItem = findNavItemByPath(navData, location.pathname);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchItems = useCallback(() => {
    setLoading(true);
    Axios.get(`${BACKEND_URL}training-essentials`, {
      params: {
        page: page + 1,
        limit: ROWS_PER_PAGE,
        search: search || undefined,
        type: selectedType !== "all" ? selectedType : undefined,
        sortField: sortOrder.name || undefined,
        sortOrder: sortOrder.direction || undefined,
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setTotalCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, page, search, selectedType, sortOrder]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchItems();
  }, [fetchItems]);

  const deleteItem = () => {
    setLoading(true);
    Axios.delete(`${BACKEND_URL}training-essentials/${deleteState.id}`)
      .then((res) => {
        toast.success(res?.data?.message || "Deleted successfully");
        setDeleteState({ status: false, id: "" });
        fetchItems();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const toggleStatus = (id, currentActive) => {
    setLoading(true);
    const request = currentActive
      ? Axios.patch(`${BACKEND_URL}training-essentials/${id}/deactivate`)
      : Axios.patch(`${BACKEND_URL}training-essentials/${id}/activate`);

    request
      .then((res) => {
        toast.success(res?.data?.message || "Status updated");
        fetchItems();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      name: "bannerImage",
      label: "Banner Image",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const img = rows[i]?.bannerImage;
          return img ? (
            <img
              src={img}
              alt=""
              style={{ width: 70, height: 45, objectFit: "cover", borderRadius: 4, backgroundColor: "#f7f7f7", border: "1px solid #ddd" }}
            />
          ) : (
            <Box sx={{ width: 70, height: 45, borderRadius: 1, bgcolor: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", color: "#666" }}>
              N/A
            </Box>
          );
        },
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        customBodyRenderLite: (i) => rows[i]?.name || "-",
      },
    },
    {
      name: "brand",
      label: "Brand",
      options: {
        customBodyRenderLite: (i) => rows[i]?.brand || "-",
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        customBodyRenderLite: (i) =>
          rows[i]?.type ? (
            <Chip label={rows[i]?.type} color="primary" sx={{ fontSize: "0.875rem", fontWeight: 600, height: 28 }} />
          ) : (
            "-"
          ),
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        customBodyRenderLite: (i) => (rows[i]?.price !== undefined ? `$${rows[i]?.price}` : "-"),
      },
    },
    {
      name: "taxRate",
      label: "Tax Rate",
      options: {
        customBodyRenderLite: (i) => `${rows[i]?.taxRate || 0}%`,
      },
    },
    {
      name: "tags",
      label: "Tags",
      options: {
        sort: false,
        customBodyRenderLite: (i) =>
          Array.isArray(rows[i]?.tags) && rows[i].tags.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {rows[i].tags.map((tag) => (
                <Chip key={tag} label={tag} variant="outlined" sx={{ fontSize: "0.875rem", fontWeight: 600, height: 28 }} />
              ))}
            </Box>
          ) : (
            "-"
          ),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => (rows[i]?.is_active ? "Active" : "Inactive"),
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(rows[i]?.is_active)}
              disabled={loading}
              onChange={() => toggleStatus(rows[i]?._id, rows[i]?.is_active)}
              size="small"
            />
            <Edit sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }} onClick={() => navigate(`/training-essentials/edit/${rows[i]?._id}`)} />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setDeleteState({ status: true, id: rows[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Training Essentials Management</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Training Essentials Management</h2>
        {write && (
          <Button variant="contained" onClick={() => navigate("/training-essentials/add")}>
            Create Training Essential
          </Button>
        )}
      </div>

      <Tabs
        value={selectedType}
        onChange={(_, val) => {
          setPage(0);
          setSelectedType(val);
        }}
        className="mb-4"
        sx={{
          "& button": {
            color: "#000",
            fontWeight: 700,
            fontSize: "1.4rem",
            textTransform: "uppercase",
            transition: "all 0.3s",
          },
          "& button:hover": {
            color: "#3dd2cc",
          },
          "& button.Mui-selected": {
            color: "#3dd2cc",
            fontWeight: "bold",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "black",
            height: "3px",
          },
        }}
      >
        <Tab className="fs-14 fw-600 p-2 mr-2" label="ALL" value="all" />
        {TRAINING_ESSENTIAL_TYPES.map((type) => (
          <Tab className="fs-14 fw-600 p-2 mr-2" key={type} label={type.toUpperCase()} value={type} />
        ))}
      </Tabs>

      <MUIDataTable
        title="Training Essentials"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          viewColumns: false,
          download: false,
          serverSide: true,
          search: true,
          searchText: searchInput,
          searchPlaceholder: "Search by name, brand, description, tag, type",
          count: totalCount,
          page,
          sortOrder,
          rowsPerPage: ROWS_PER_PAGE,
          rowsPerPageOptions: [],
          textLabels: { body: { noMatch: loading ? "Loading..." : "No records found" } },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
            } else if (action === "sort") {
              setSortOrder(tableState.sortOrder);
              setPage(0);
            }
          },
          onSearchChange: (searchQuery) => {
            setSearchInput(searchQuery || "");
          },
          onSearchClose: () => {
            setSearchInput("");
          },
        }}
      />

      <Dialog open={deleteState.status} onClose={() => setDeleteState({ status: false, id: "" })} fullWidth>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete Record</h3>
          <IconButton onClick={() => setDeleteState({ status: false, id: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeleteState({ status: false, id: "" })}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" disabled={loading} onClick={deleteItem}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TrainingEssentialsManagement;